'use strict';

function mpv3ShouldOpenCartDrawerAfterAdd(element) {
  const owner = element?.closest('[data-open-cart-drawer-after-add]');
  return owner?.dataset.openCartDrawerAfterAdd === 'true';
}

function mpv3OpenCartDrawerAfterAdd() {
  if (typeof window.updateCartDrawer === 'function') {
    window.updateCartDrawer();
    return;
  }

  const cartDrawer = document.querySelector('cart-drawer');
  document.dispatchEvent(new CustomEvent('cart:refresh'));

  if (!cartDrawer) return;

  if (typeof cartDrawer.show === 'function') {
    cartDrawer.show();
  } else if (typeof cartDrawer.open === 'function') {
    cartDrawer.open();
  } else {
    cartDrawer.classList.add('active', 'animate');
    cartDrawer.setAttribute('open', '');
  }
}

/**
 * Main Product V3 Section JavaScript
 * Custom Elements for gallery, plan CTA, accordion, and selling plans
 */

/* ==========================================================================
   Gallery Component
   ========================================================================== */
if (!customElements.get('mpv1-gallery')) {
  class Mpv1Gallery extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.thumbnails = [];
      this.touchStartX = 0;
      this.touchEndX = 0;
    }

    connectedCallback() {
      this.mainImage = this.querySelector('.mpv1-gallery__main-img');
      this.thumbnails = Array.from(this.querySelectorAll('.mpv1-gallery__thumb'));
      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      this.mainWrapper = this.querySelector('.mpv1-gallery__main');
      this.thumbsContainer = this.querySelector('.mpv1-gallery__thumbs');

      if (!this.mainImage || this.thumbnails.length === 0) return;

      // Find initial active thumbnail
      this.currentIndex = this.thumbnails.findIndex(thumb => thumb.classList.contains('is-active'));
      if (this.currentIndex === -1) this.currentIndex = 0;

      this.bindEvents();
      this.updateNavButtons();
    }

    bindEvents() {
      // Thumbnail clicks
      this.thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.goToSlide(index));
      });

      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      // Keyboard navigation
      this.addEventListener('keydown', (e) => this.handleKeydown(e));

      // Touch/swipe support on main image wrapper
      if (this.mainWrapper) {
        this.mainWrapper.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
        this.mainWrapper.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
      }
    }

    handleKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    }

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    }

    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }

    handleSwipe() {
      const swipeThreshold = 50;
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) < swipeThreshold) return;

      if (diff > 0) {
        // Swipe left - go next
        this.next();
      } else {
        // Swipe right - go prev
        this.prev();
      }
    }

    prev() {
      if (this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1);
      }
    }

    next() {
      if (this.currentIndex < this.thumbnails.length - 1) {
        this.goToSlide(this.currentIndex + 1);
      }
    }

    goToSlide(index) {
      if (index < 0 || index >= this.thumbnails.length) return;

      const thumb = this.thumbnails[index];
      const fullSrc = thumb.dataset.full;
      const srcset = thumb.dataset.srcset;
      const alt = thumb.dataset.alt || '';

      if (!fullSrc) return;

      // Update main image — set srcset before src to avoid a double network
      // request: if src fires first the browser starts fetching the 1200px
      // image, then cancels/restarts when srcset is evaluated a tick later.
      this.mainImage.classList.add('is-loading');
      const onLoad = () => {
        this.mainImage.classList.remove('is-loading');
        this.mainImage.removeEventListener('load', onLoad);
      };
      this.mainImage.addEventListener('load', onLoad);

      if (srcset) {
        this.mainImage.srcset = srcset;
      }
      this.mainImage.src = fullSrc;
      this.mainImage.alt = alt;

      // Update active states
      this.thumbnails.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      this.currentIndex = index;
      this.updateNavButtons();

      // Scroll active thumbnail into view — scroll the strip container directly
      // to avoid scrollIntoView bubbling up to the viewport and shifting the layout.
      if (this.thumbsContainer) {
        const scrollLeft = thumb.offsetLeft - (this.thumbsContainer.offsetWidth / 2) + (thumb.offsetWidth / 2);
        this.thumbsContainer.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }

    updateNavButtons() {
      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentIndex === 0;
        this.prevBtn.setAttribute('aria-disabled', this.currentIndex === 0 ? 'true' : 'false');
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = this.currentIndex === this.thumbnails.length - 1;
        this.nextBtn.setAttribute('aria-disabled', this.currentIndex === this.thumbnails.length - 1 ? 'true' : 'false');
      }
    }
  }

  customElements.define('mpv1-gallery', Mpv1Gallery);
}

/* ==========================================================================
   Pack Selector Component
   Quantity-based bundle picker — single variant, no selling plans.
   Each card has its own ORDER NOW button. Clicking it directly adds that
   plan's quantity to cart and redirects to checkout.
   Passes supply_plan + rebill_days as line item properties so the
   external subscription platform (Phoenix / Checkout Champ) can read
   them at checkout.
   ========================================================================== */
if (!customElements.get('mpv3-pack-selector')) {
  class Mpv3PackSelector extends HTMLElement {
    constructor() {
      super();
      this.loadingBtn = null;
      this.errorEl = null;
      this.layoutType = null;
    }

    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.layoutType = this.dataset.layout || 'cards';
      this.errorEl = this.querySelector('.mpv3-pack__error');

      if (!this.variantId) return;

      if (this.layoutType === 'switchers') {
        this._initSwitchers();
      } else {
        this._initCards();
      }
    }

    _initCards() {
      this.cards = Array.from(this.querySelectorAll('.mpv3-pack'));
      if (this.cards.length === 0) return;

      // Each card has its own CTA button — bind per-card
      this.cards.forEach(card => {
        const btn = card.querySelector('.mpv3-pack__cta-btn');
        if (btn) {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._handleAddToCartFromCard(card, btn);
          });
        }
      });
    }

    _initSwitchers() {
      this.radios = Array.from(this.querySelectorAll('.mpv3-switcher__radio'));
      this.switcherBtn = this.querySelector('.mpv3-switchers__cta-btn');
      this.switcherBtnText = this.querySelector('.mpv3-switchers__cta-text');
      this.baseButtonText = this.switcherBtnText ? this.switcherBtnText.textContent.trim() : 'ADD TO CART';

      if (this.radios.length === 0 || !this.switcherBtn) return;

      // Select default option
      const defaultOption = this.querySelector('[data-default] .mpv3-switcher__radio');
      if (defaultOption) {
        defaultOption.checked = true;
      } else if (this.radios[0]) {
        this.radios[0].checked = true;
      }

      // Default subscription checkbox checked on page load
      this._setSubscriptionCheckboxDefaultChecked();

      // Listen for subscription checkbox change and refresh PDP display prices
      this._initCopySubscriptionPriceToggle();

      // Set initial button text, href and display prices
      this._updateSwitcherSelection();

      // Update selection state on radio change
      this.radios.forEach(radio => {
        radio.addEventListener('change', () => {
          this._updateSwitcherSelection();
        });
      });

      // Add to cart and open native cart drawer instead of navigating to checkout URL
      this.switcherBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        this._handleAddToCartFromSwitcher(this.switcherBtn);
      }, true);
    }

    _setSubscriptionCheckboxDefaultChecked() {
      const subscriptionToggle = this.querySelector('[data-mpv3-subscription-toggle]');

      if (!subscriptionToggle) return;

      // Default subscription checkbox unchecked on page load.
      // User can manually check it before clicking Add To Cart.
      subscriptionToggle.checked = false;
      subscriptionToggle.defaultChecked = false;
      subscriptionToggle.removeAttribute('checked');
      subscriptionToggle.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _initCopySubscriptionPriceToggle() {
      const subscriptionToggle = this.querySelector('[data-mpv3-subscription-toggle]');

      if (!subscriptionToggle) return;

      subscriptionToggle.addEventListener('change', () => {
        this._updateSwitcherSelection();
      });
    }

    _isCopySubscriptionDisplayDiscountActive() {
      const subscriptionToggle = this.querySelector('[data-mpv3-subscription-toggle]');

      return Boolean(
        this._isCopySubscriptionPdp() &&
        subscriptionToggle &&
        subscriptionToggle.checked
      );
    }

    _getDisplayPricingForCard(card) {
      const baseTotalPrice = card ? (card.dataset.totalPrice || '') : '';
      const baseComparePrice = card ? (card.dataset.compareTotalPrice || '') : '';
      const baseUnitPrice = card ? (card.dataset.unitPrice || '') : '';

      if (!this._isCopySubscriptionDisplayDiscountActive()) {
        return {
          totalPrice: baseTotalPrice,
          comparePrice: baseComparePrice,
          unitPrice: baseUnitPrice
        };
      }

      const discountedTotalPrice = this._discountMoneyString(baseTotalPrice, 5);
      const discountedUnitPrice = this._discountMoneyString(baseUnitPrice, 5);

      return {
        totalPrice: discountedTotalPrice || baseTotalPrice,
        comparePrice: baseComparePrice,
        unitPrice: discountedUnitPrice || baseUnitPrice
      };
    }

    _refreshCopySubscriptionCardPrices() {
      if (!this._isCopySubscriptionPdp()) return;

      const cards = Array.from(this.querySelectorAll('.mpv3-switcher'));

      cards.forEach((card) => {
        const pricing = this._getDisplayPricingForCard(card);

        const totalAmountEl = card.querySelector('.mpv3-switcher__total-amount');
        if (totalAmountEl && pricing.totalPrice) {
          totalAmountEl.textContent = pricing.totalPrice;
        }

        const compareEl = card.querySelector('.mpv3-switcher__compare');
        if (compareEl) {
          if (pricing.comparePrice) {
            compareEl.textContent = pricing.comparePrice;
            compareEl.hidden = false;
            compareEl.style.display = '';
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
            compareEl.style.display = 'none';
          }
        }

        const unitPriceEl = card.querySelector('.mpv3-switcher__unit-price strong');
        if (unitPriceEl && pricing.unitPrice) {
          unitPriceEl.textContent = pricing.unitPrice;
        }
      });
    }

    _discountMoneyString(value, discountPercent) {
      const parts = this._parseMoneyString(value);

      if (!parts) return '';

      const discountedAmount = parts.amount * (1 - (discountPercent / 100));

      return this._formatMoneyString(parts, discountedAmount);
    }

    _parseMoneyString(value) {
      const raw = String(value || '').trim();
      if (!raw) return null;

      const match = raw.match(/-?\d[\d,]*(?:\.\d+)?/);
      if (!match) return null;

      const numberText = match[0];
      const amount = parseFloat(numberText.replace(/,/g, ''));

      if (!Number.isFinite(amount)) return null;

      return {
        raw,
        amount,
        numberText,
        prefix: raw.slice(0, match.index),
        suffix: raw.slice(match.index + numberText.length),
        hadDecimals: numberText.indexOf('.') !== -1
      };
    }

    _formatMoneyString(parts, amount) {
      const roundedAmount = Math.round(amount * 100) / 100;
      const hasCents = Math.abs(roundedAmount % 1) > 0.001;
      const decimalPlaces = parts.hadDecimals || hasCents ? 2 : 0;
      const formattedAmount = roundedAmount.toLocaleString('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
      });

      return `${parts.prefix}${formattedAmount}${parts.suffix}`;
    }

    _updateSwitcherSelection() {
      // Find selected radio and get the total price from parent label
      const selectedRadio = this.querySelector('.mpv3-switcher__radio:checked');
      if (!selectedRadio) return;

      const label = selectedRadio.closest('label');
      const pricing = this._getDisplayPricingForCard(label);
      const totalPrice = pricing.totalPrice || '';
      const comparePrice = pricing.comparePrice || '';
      const checkoutUrl = label ? label.dataset.checkoutUrl : '';

      this._refreshCopySubscriptionCardPrices();

      // Update button text to show: "ADD TO CART - $149.70"
      if (this.switcherBtnText) {
        if (totalPrice) {
          this.switcherBtnText.innerHTML = `${this.baseButtonText} - ${totalPrice} ${
  comparePrice ? `<span class="compare-price">${comparePrice}</span>` : ''
}`;
        } else {
          this.switcherBtnText.textContent = this.baseButtonText;
        }
      }

      // Update Standalone Price Rows if they exist
      const currentEls = document.querySelectorAll('.js-mpv3-price-current');
      const oldEls = document.querySelectorAll('.js-mpv3-price-old');
      const saleEls = document.querySelectorAll('.js-mpv3-price-sale');

      currentEls.forEach(el => { el.textContent = totalPrice; });
      oldEls.forEach(el => {
        el.textContent = comparePrice;
        el.style.display = comparePrice ? 'inline' : 'none';
      });

      saleEls.forEach(el => {
        const template = el.dataset.saleTemplate;
        if (!template) return;

        // Restore the safe placeholders back to readable form for matching
        let saleText = template;
        const subtitle = label ? (label.dataset.subtitle || '') : '';
        const badgeText = label ? (label.dataset.badge || '') : '';

        // 1. Discount Amount: calculate from price data
        const rawCurrentPrice = totalPrice || '';
        const rawComparePrice = comparePrice || '';
        if (rawCurrentPrice && rawComparePrice) {
            const currentNum = parseFloat(rawCurrentPrice.replace(/[^0-9.]/g, ''));
            const compareNum = parseFloat(rawComparePrice.replace(/[^0-9.]/g, ''));
            if (!isNaN(currentNum) && !isNaN(compareNum) && compareNum > currentNum) {
                const discount = compareNum - currentNum;
                let discountStr = discount.toFixed(2).replace(/\.00$/, '');
                if (rawCurrentPrice.includes('$')) discountStr = `$${discountStr}`;
                saleText = saleText.replace(/__OPEN__\s*discount_amount\s*__CLOSE__/g, discountStr);
            }
        }

        // 2. Plan subtitle (e.g. "You save 50%")
        saleText = saleText.replace(/__OPEN__\s*discount_subtitle\s*__CLOSE__/g, subtitle);

        // 3. Plan badge (e.g. "Best Value")
        saleText = saleText.replace(/__OPEN__\s*plan_badge\s*__CLOSE__/g, badgeText);

        // Show badge only if all placeholders were replaced
        const hasUnfilled = saleText.includes('__OPEN__');
        if (hasUnfilled || saleText.trim() === '') {
            el.style.display = 'none';
        } else {
            el.textContent = saleText;
            el.style.display = 'inline-block';
        }
      });

      // Update anchor href to checkout URL from selected option
      if (this.switcherBtn) {
  this.switcherBtn.href = '#';
  this.switcherBtn.dataset.selectedCheckoutUrl = checkoutUrl || '';
}
    }

    async _handleAddToCartFromCard(card, btn) {
  if (btn.classList.contains('is-loading')) return;

  this._clearError();
  this._setLoading(btn, true);

  const qty = parseInt(card.dataset.qty, 10) || 1;
  const supplyPlan = card.dataset.supplyPlan || '';
  const rebillDays = card.dataset.rebillDays || '';

  const selectedCartItem = {
    id: parseInt(this.variantId, 10),
    quantity: qty
  };

  await this._addToCart(selectedCartItem, supplyPlan, rebillDays, btn);
}

_isCopySubscriptionPdp() {
  return this.dataset.isCopySubscriptionPdp === 'true';
}

_getSelectedPlanIndex() {
  const selectedRadio = this.querySelector('.mpv3-switcher__radio:checked');

  if (selectedRadio) {
    const radioValue = parseInt(selectedRadio.value, 10);

    if (radioValue >= 1 && radioValue <= 3) {
      return radioValue;
    }

    const wrap = selectedRadio.closest('.mpv3-switcher-wrap');
    const planValue = wrap ? parseInt(wrap.dataset.plan, 10) : null;

    if (planValue >= 1 && planValue <= 3) {
      return planValue;
    }
  }

  return 1;
}

_getSafeVariantId(value) {
  const match = String(value || '').trim().match(/\d{8,}/);
  if (!match) return null;

  const variantId = parseInt(match[0], 10);
  return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
}

_getCopySelectedCartItem() {
  if (!this._isCopySubscriptionPdp()) return null;

  const planIndex = this._getSelectedPlanIndex();

  const variantAttrName = `data-copy-plan-${planIndex}-variant-id`;
  const selectedVariantId = this._getSafeVariantId(
    this.getAttribute(variantAttrName)
  );

  if (!selectedVariantId) {
    console.error('[mpv3-pack-selector] Copy PDP variant ID missing.', {
      planIndex,
      variantAttrName,
      value: this.getAttribute(variantAttrName)
    });

    return null;
  }

  return {
    id: selectedVariantId,
    quantity: 1
  };
}

_getSellingPlanId() {
  if (this._isCopySubscriptionPdp()) {
    const subscriptionToggle = this.querySelector('[data-mpv3-subscription-toggle]');

    if (!subscriptionToggle || !subscriptionToggle.checked) {
      return null;
    }

    const sellingPlanId = this._getSafeVariantId(
      this.getAttribute('data-copy-selling-plan-id')
    );

    return sellingPlanId || null;
  }

  const input = this.querySelector('[data-selling-plan-new-id]');
  const value = input ? input.value : '';
  const sellingPlanId = parseInt(value, 10);

  return Number.isFinite(sellingPlanId) && sellingPlanId > 0 ? sellingPlanId : null;
}

_getCartItemsFromCheckoutUrl(url) {
  if (!url || url === '#') return [];

  let parsedUrl;

  try {
    parsedUrl = new URL(url, window.location.origin);
  } catch (e) {
    console.error('[mpv3-pack-selector] Invalid checkout URL:', url);
    return [];
  }

  if (parsedUrl.pathname.indexOf('/cart/') === -1) return [];

  const cartPart = parsedUrl.pathname.split('/cart/')[1];
  if (!cartPart) return [];

  return cartPart
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
    .map(item => {
      const cleanItem = item.split('?')[0];
      const parts = cleanItem.split(':');

      const variantId = parseInt(parts[0], 10);
      const quantity = parseInt(parts[1] || '1', 10) || 1;

      if (!variantId) return null;

      return {
        id: variantId,
        quantity
      };
    })
    .filter(Boolean);
}

getCartItemFromCheckoutUrl(url) {
  // Copy PDP ignores checkout URL and uses separate Customize settings.
  if (this._isCopySubscriptionPdp()) {
    return this._getCopySelectedCartItem();
  }

  const cartItems = this._getCartItemsFromCheckoutUrl(url);
  return cartItems[0] || null;
}

async _handleAddToCartFromSwitcher(btn) {
  if (btn.classList.contains('is-loading')) return;

  const selectedRadio = this.querySelector('.mpv3-switcher__radio:checked');

  if (!selectedRadio) {
    this._showError('Please select a supply option');
    return;
  }

  this._clearError();
  this._setLoading(btn, true);

  const label = selectedRadio.closest('label');

  const supplyPlan = label ? (label.dataset.supplyPlan || '') : '';
  const rebillDays = label ? (label.dataset.rebillDays || '') : '';

  const checkoutUrl =
    btn.dataset.selectedCheckoutUrl ||
    (label ? label.dataset.checkoutUrl : '') ||
    '';

  let selectedCartItem;

  if (this._isCopySubscriptionPdp()) {
    selectedCartItem = this._getCopySelectedCartItem();
  } else {
    selectedCartItem = this.getCartItemFromCheckoutUrl(checkoutUrl);
  }

  if (!selectedCartItem && !this._isCopySubscriptionPdp()) {
    const fallbackQty = label ? (parseInt(label.dataset.qty, 10) || 1) : 1;
    const fallbackVariantId = parseInt(this.variantId, 10);

    if (fallbackVariantId) {
      selectedCartItem = {
        id: fallbackVariantId,
        quantity: fallbackQty
      };
    }
  }

  if (!selectedCartItem) {
    this._setLoading(btn, false);

    if (this._isCopySubscriptionPdp()) {
      this._showError('Please add the selected plan variant ID in Customize settings.');
    } else {
      this._showError('Selected plan variant is missing or invalid.');
    }

    console.error('[mpv3-pack-selector] Invalid selected variant.', {
      isCopySubscriptionPdp: this._isCopySubscriptionPdp(),
      selectedPlanIndex: this._getSelectedPlanIndex(),
      checkoutUrl
    });

    return;
  }

  await this._addToCart(selectedCartItem, supplyPlan, rebillDays, btn, checkoutUrl);
}

_buildCartItem(sourceItem, supplyPlan, rebillDays) {
  const cartItem = {
    id: parseInt(sourceItem.id, 10),
    quantity: parseInt(sourceItem.quantity, 10) || 1,
    properties: {}
  };

  if (!cartItem.id) return null;

  const sellingPlanId = this._getSellingPlanId();

  // Same as working V1 template:
  // add only first selected variant + Shopify selling_plan.
  if (sellingPlanId) {
    cartItem.selling_plan = sellingPlanId;
  }

  if (supplyPlan) cartItem.properties['_supply_plan'] = supplyPlan;
  if (rebillDays) cartItem.properties['_rebill_days'] = rebillDays;

  if (Object.keys(cartItem.properties).length === 0) {
    delete cartItem.properties;
  }

  return cartItem;
}

async _postAddToCart(cartItem) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      items: [cartItem]
    })
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = data.description || data.message || data.status || 'Could not add to cart';
    const error = new Error(message);
    error.response = data;
    error.status = response.status;
    throw error;
  }

  return data;
}

async _addToCart(selectedCartItem, supplyPlan, rebillDays, btn, checkoutUrl = '') {
  const cartItem = this._buildCartItem(selectedCartItem, supplyPlan, rebillDays);

  if (!cartItem) {
    this._setLoading(btn, false);
    this._showError('Selected plan variant is missing or invalid.');
    return;
  }

  console.log('[mpv3-pack-selector] Adding selected cart item:', cartItem);

  try {
    const data = await this._postAddToCart(cartItem);

    this._setLoading(btn, false);

    if (mpv3ShouldOpenCartDrawerAfterAdd(this)) {
      mpv3OpenCartDrawerAfterAdd();
      return;
    }

    window.location.href = '/checkout';
  } catch (err) {
    console.error('[mpv3-pack-selector] Add to cart error:', err);
    console.error('[mpv3-pack-selector] Shopify response:', err.response || 'No JSON response');
    console.error('[mpv3-pack-selector] Item sent:', cartItem);

    this._setLoading(btn, false);
    this._showError(err.message || 'Something went wrong. Please try again.');
  }
}
    _setLoading(btn, loading) {
      btn.classList.toggle('is-loading', loading);
      btn.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.loadingBtn = loading ? btn : null;
    }

    _showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }
    }

    _clearError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  customElements.define('mpv3-pack-selector', Mpv3PackSelector);
}

/* ==========================================================================
   Accordion Component
   ========================================================================== */
if (!customElements.get('mpv1-accordion')) {
  class Mpv1Accordion extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
    }

    connectedCallback() {
      this.trigger = this.querySelector('.mpv1-accordion__trigger');
      this.panel = this.querySelector('.mpv1-accordion__panel');
      this.content = this.querySelector('.mpv1-accordion__content');

      if (!this.trigger || !this.panel) return;

      // Set initial ARIA attributes
      const triggerId = `mpv1-accordion-trigger-${this.generateId()}`;
      const panelId = `mpv1-accordion-panel-${this.generateId()}`;

      this.trigger.setAttribute('id', triggerId);
      this.trigger.setAttribute('aria-controls', panelId);
      this.trigger.setAttribute('aria-expanded', 'false');

      this.panel.setAttribute('id', panelId);
      this.panel.setAttribute('aria-labelledby', triggerId);
      this.panel.setAttribute('role', 'region');

      this.trigger.addEventListener('click', () => this.toggle());

      // Support keyboard
      this.trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggle();
        }
      });
    }

    generateId() {
      return Math.random().toString(36).substring(2, 9);
    }

    toggle() {
      this.isOpen = !this.isOpen;
      this.classList.toggle('is-open', this.isOpen);
      this.trigger.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');

      if (this.content) {
        if (this.isOpen) {
          // Measure actual content height before opening so the CSS transition
          // animates to the correct pixel value (P1 fix: stale height on close).
          const height = this.content.scrollHeight;
          this.style.setProperty('--mpv1-accordion-height', `${height}px`);
        } else {
          // Reset the height var on close so a subsequent open re-measures correctly.
          this.style.removeProperty('--mpv1-accordion-height');
        }
      }
    }

    open() {
      if (!this.isOpen) this.toggle();
    }

    close() {
      if (this.isOpen) this.toggle();
    }
  }

  customElements.define('mpv1-accordion', Mpv1Accordion);
}
